import {
  isValidElement,
  useEffect,
  useContext,
  useCallback,
  useState
} from 'react';
import { trackEvent, TrackingContext } from '@gjensidige/core-analytics';
import type { TrackEvent } from '@gjensidige/core-analytics';
import type { TrackingProp } from './types';
import helpStyles from './help-text/help-text.module.css';
import validationStyles from './validation-message/validation-message.module.css';
import { name, version } from '../package.json';
import '@gjensidige/builders-tokens/tokens.css';
import './utils.css';

declare global {
  interface Window {
    buildersUUID?: number;
  }
}

export const IS_BROWSER =
  typeof window !== 'undefined' &&
  typeof window.document !== 'undefined' &&
  typeof window.navigator !== 'undefined';

export const EXTERNAL_TEXTS = {
  no: 'Åpnes i nytt vindu',
  da: 'Åbner i nyt vindue',
  sv: 'Öppnas i nytt fönster',
  en: 'Opens in new window'
};

// Add aria-description to external links (switch to this in Link and Card when aria-description is supported)
// Here is the version of code using this: https://github.com/gjensidige/builders-core/blob/1aadfddeda0ecc024aecaf46c4a8ddc34d4a5b6b/packages/builders-components/src/link/link.tsx
/* export function useExternalLinkText(
  ref: React.RefObject<unknown>,
  isExternal: boolean
) {
  useEffect(() => {
    const self = ref.current as Element | null;
    const lang = isExternal && self?.closest('[lang]')?.getAttribute('lang');
    const text = EXTERNAL_TEXTS[lang as keyof typeof EXTERNAL_TEXTS];

    if (text) self?.setAttribute('aria-description', text);
    else if (isExternal) console.warn(`Invalid page language "${lang}"`);
    return () => self?.removeAttribute('aria-description');
  }, [ref, isExternal]);
} */

// useId is a new hook in React 18, and we need to support 16.8 and upwards
// In this polyfill, always return temp_id on first render to avoid server/client-hyration mismatch
// and on DOM mount we return a new ID, causing a re-render, but only once, and only to change the ID
// (instead of doing a full re-hydration as we otherwise would have).
// We also build UUID based on a global variable, to avoid conflictin IDs
export function useId() {
  const [id, setId] = useState<undefined | string>(() => undefined);
  useEffect(() => {
    setId(`:b${(window.buildersUUID = (window.buildersUUID || 0) + 1)}`);
  }, []);
  return id;
}

export function useReqireLabel<T>(ref: React.RefObject<T>) {
  useEffect(() => {
    // Let react render first and not block thread
    const timeoutId = setTimeout(() => {
      if (ref.current instanceof HTMLElement) {
        const self = ref.current;
        const label =
          self.hasAttribute('aria-label') || // aria-label is a valid alternative to <label>
          self.closest('label') ||
          (self.id && document.querySelector(`label[for="${self.id}"]`));
        const isTableInternal = self.closest('table'); // this will turn of warning for all inputs inside tables, but probably only happens in internal applications

        if (!label && !isTableInternal)
          console.warn(
            'Form element missing <Label>. Please set an id={exampleId} and corresponding <Label htmlFor={exampleId}>:',
            self
          );
      }
    }, 300);
    return () => clearTimeout(timeoutId); // In case we get a new htmlFor selector by i.e. useId()
  }, [ref]);
}

export function useTracking(tracking: TrackingProp) {
  const { options } = useContext(TrackingContext);

  return useCallback(
    (dynamic?: TrackEvent, overwrite = {}) => {
      if (tracking)
        trackEvent(
          {
            eventVersion: version,
            eventSource: name,
            eventLabel: '',
            ...dynamic,
            ...(tracking === true ? {} : tracking),
            ...overwrite
          },
          options
        );
    },
    [tracking, options]
  );
}

export const toReactProps = <T>(el: unknown): T => {
  const isReactProps = el && typeof el === 'object' && !isValidElement(el);
  return (isReactProps ? el : { children: el }) as T;
};

export const animateHidden = (
  self: HTMLElement,
  hide?: unknown,
  tableRow?: HTMLTableRowElement | null
) =>
  new Promise((resolve) => {
    const isUnsupported = !Element.prototype.animate; // Because the widely used testing library jsdom does not support .animate
    const isNotAllowed = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    ).matches; // jsdom does not support .watchMedia
    if (isNotAllowed || isUnsupported) return resolve(self);
    self.removeAttribute('hidden'); // Remove hidden to measure height
    const { scrollWidth: w, scrollHeight: h } = self;
    const options = { duration: 400, easing: 'ease-in-out' };
    const timeline = {
      height: [hide ? `${h}px` : '0px', hide ? '0px' : `${h}px`],
      paddingBlock: [hide ? '' : '0px', hide ? '0px' : ''],
      width: [`${w}px`, `${w}px`] // Set width in case table cell
    };

    self.style.overflow = 'clip'; // Clip content while animating
    if (tableRow) self.style.position = 'absolute'; // TableCells can not animate height unless position: absolute
    if (tableRow) tableRow.animate(timeline, options); // TableRow element also need to animate

    self.animate(timeline, options).onfinish = () => {
      if (hide) self.setAttribute('hidden', '');
      else self.removeAttribute('hidden');
      self.style.overflow = self.style.position = ''; // Restore overflow
      resolve(self);
    };
  });

export function getText(el: unknown) {
  if (!(el instanceof HTMLElement)) return '';
  return (el.innerText || el.textContent || el.getAttribute('aria-label') || '')
    .replace(/\n/g, ' ')
    .trim();
}

export function getLabel(el: unknown) {
  if (el instanceof Element && 'labels' in el) el = (el.labels as NodeList)[0]; // Both input, select, progress and textarea can have labels
  if (!(el instanceof HTMLLabelElement || el instanceof HTMLLegendElement))
    return ''; // But only track if we actually found a true label or legend element

  const help = getText(el.querySelector(helpStyles.helpText));
  const validation = getText(el.querySelector(validationStyles.message));
  return getText(el).replace(help, '').replace(validation, ''); // As label is the wrapping element, remove inner texts
}

export function onRoleButtonKeyDown(event: React.KeyboardEvent<Element>) {
  if (event.key === ' ' || event.key === 'Enter') {
    const click = new MouseEvent('click', { bubbles: true, cancelable: true });
    event.preventDefault(); // Prevent scroll
    event.currentTarget.dispatchEvent(click); // Using .dispatchEvent as SVG elements does not support .click
  }
}

// Use setup callback to run additional callbacks to associated elements
export const useDescribedby = (
  ref: React.RefObject<Element>,
  selector?: string | false,
  setup: (el: Element) => ((el: Element) => void) | undefined = () => undefined,
  placement: 'first' | 'last' = 'last'
) =>
  useEffect(() => {
    let targets: Element[] = []; // Cache target for clean up
    const self = ref.current;
    const cleanup: ReturnType<typeof setup>[] = [];
    const describedbyIds = (el?: Element | null) =>
      (el?.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);

    // Let React render first and not block thread
    const timeoutId = setTimeout(() => {
      if (selector) targets = [...document.querySelectorAll(selector)];
      if (self)
        targets.forEach((el: Element) => {
          const addSelfId = describedbyIds(el);
          addSelfId[placement === 'first' ? 'unshift' : 'push'](self.id);
          const addSelfUniqueId = Array.from(new Set(addSelfId));

          el.setAttribute('aria-describedby', addSelfUniqueId.join(' ').trim());
          cleanup.push(setup(el));
        });

      if (self?.tagName !== 'LABEL' && self?.closest('label'))
        console.warn(
          self,
          `is placed inside a <label>. Please move it out and use the htmlFor="ID-of-input-here" instead to enhance accessibility.`
        );
    }, 300);

    return () => {
      clearTimeout(timeoutId); // In case we get a new htmlFor selector by i.e. useId()
      targets.forEach((el, idx) => {
        const removeSelfId = describedbyIds(el).filter((id) => id !== self?.id);
        el.setAttribute('aria-describedby', removeSelfId.join(' ').trim());
        cleanup[idx]?.(el);
      });
    };
  }, [placement, ref, selector, setup]);

// Not exposed as a hook, as it is nice to allow consuming components to use it as a callback
export function anchorPosition(
  anchorElement: Element,
  relativeElement: HTMLElement,
  align = 'left'
) {
  const rePosition = () => {
    relativeElement.style.top = relativeElement.style.left = '0px'; // Start with neutral position to allow inner text wrapping

    const { left, width, bottom } = anchorElement.getClientRects()[0]; // If text is wrapping, use first rect
    const relativeWidth = relativeElement.clientWidth;
    const hasHorizontalScroll =
      document.documentElement.scrollWidth > window.innerWidth; // Correct scroll position if inside scrollable table

    let x: number;
    if (align === 'left' || align === 'start') x = left;
    else if (align === 'center') x = left - (relativeWidth - width) / 2;
    else x = left + width - relativeWidth;

    const relativeLeft = Math.min(
      Math.max(hasHorizontalScroll ? -Infinity : 10, x),
      hasHorizontalScroll ? Infinity : window.innerWidth - relativeWidth - 10
    );

    const parent = relativeElement.parentElement;
    const modalY = (parent?.nodeName === 'DIALOG' && parent?.scrollTop) || 0; // Correct scroll position if inside <Modal>
    const anchorCenter = Math.round(left + width / 2 - relativeLeft);

    relativeElement.style.left = `${Math.round(relativeLeft)}px`;
    relativeElement.style.top = `${Math.round(bottom) + modalY}px`;
    relativeElement.style.setProperty('--anchor-center', `${anchorCenter}px`);

    // To position <Datepicker> arrow
    const diff = left - relativeLeft;
    const leftOffset = Math.round(diff > 0 ? diff : 0);
    relativeElement.style.setProperty('--anchor-offset', `${leftOffset}px`);
  };

  document.fonts?.ready.then(rePosition); // Reposistion after fonts are loaded
  document.addEventListener('DOMContentReady', rePosition);
  window.addEventListener('scroll', rePosition, true); // Use capture to also listen for elements with overflow
  window.addEventListener('resize', rePosition);
  window.addEventListener('load', rePosition);

  return () => {
    document.removeEventListener('DOMContentReady', rePosition);
    window.removeEventListener('scroll', rePosition, true);
    window.removeEventListener('resize', rePosition);
    window.removeEventListener('load', rePosition);
  };
}
